import { LiteracyScreeningFileListItemModel } from './LiteracyScreeningFileListItemModel';
import { LiteracyScreeningGradesModel } from './LiteracyScreeningGradesModel';
import { LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION } from './LiteracyScreeningModel';
import { LiteracyScreeningSchoolModel } from './LiteracyScreeningSchoolModel';
import { LiteracyScreeningScreenerModel } from './LiteracyScreeningScreenerModel';
import { SelectOption } from './SelectOption';
import yup from './Validation/CustomValidation/yup-extended';
import { VALIDATION_MESSAGE } from './ValidationMessage';

export const LITERACY_SCREENING_DOCUMENT_OPTION_SETS = {
  documentType: 'DocumentTypeOptions',
  invoiceLevel: 'InvoiceLevelOptions',
  screenerInvoice: 'ScreenerInvoiceOptions',
  mdeStatus: 'MDEStatusOptions',
} as const;

export const LITERACY_SCREENING_PENDING_APPROVAL_STATUS = 'Pending Approval';
export const LITERACY_SCREENING_COMMENTS_MAX_LENGTH = 100;

export const LITERACY_SCREENING_DEFAULT_MDE_STATUS_OPTIONS: SelectOption[] = [
  {
    value: LITERACY_SCREENING_PENDING_APPROVAL_STATUS,
    text: LITERACY_SCREENING_PENDING_APPROVAL_STATUS,
    isDisabled: false,
  },
  { value: 'Approved', text: 'Approved', isDisabled: false },
  { value: 'Denied', text: 'Denied', isDisabled: false },
];

export interface LiteracyScreeningBoyScreenerPair {
  schoolIdentifier: string;
  screener: string;
}

export interface LiteracyScreeningUploadFieldOptionsInput {
  documentTypeOptions: SelectOption[];
  invoiceLevelOptions: SelectOption[];
  screenerInvoiceOptions: SelectOption[];
  schools: LiteracyScreeningSchoolModel[] | undefined;
  files: LiteracyScreeningFileListItemModel[] | undefined;
  reimbursement?: string | null;
  selectedInvoiceLevel?: string | null;
  selectedSchool?: string | null;
}

export interface LiteracyScreeningUploadFieldOptions {
  documentTypeOptions: SelectOption[];
  invoiceLevelOptions: SelectOption[];
  schoolOptions: SelectOption[];
  screenerInvoiceOptions: SelectOption[];
  hasSavedBoyEntry: boolean;
}

export interface LiteracyScreeningUploadFormValues {
  documentType: string;
  invoiceLevel: string;
  school: string;
  screenerInvoice: string;
}

const normalize = (value?: string | null): string =>
  (value || '').trim().toLowerCase();

export const findLiteracyScreeningOption = (
  options: SelectOption[] | undefined,
  value?: string | null
): SelectOption | undefined => {
  const normalizedValue = normalize(value);
  if (!normalizedValue) {
    return undefined;
  }

  return (options || []).find(
    (option) =>
      normalize(option.value) === normalizedValue ||
      normalize(option.text) === normalizedValue
  );
};

const optionLabel = (
  options: SelectOption[] | undefined,
  value?: string | null
): string => {
  const option = findLiteracyScreeningOption(options, value);
  return (option?.text || option?.value || value || '').trim();
};

const compareOptionText = (left: SelectOption, right: SelectOption): number =>
  left.text.localeCompare(right.text, undefined, { sensitivity: 'base' });

const isBeginningOfYearSession = (session?: string | null): boolean =>
  normalize(session) ===
  normalize(LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION);

export const isLiteracyScreeningScreenerInvoiceDocumentType = (
  value: string | undefined | null,
  documentTypeOptions: SelectOption[] | undefined
): boolean =>
  optionLabel(documentTypeOptions, value)
    .toLowerCase()
    .includes('screener invoice');

export const isLiteracyScreeningSectionADocumentType = (
  value: string | undefined | null,
  documentTypeOptions: SelectOption[] | undefined
): boolean =>
  optionLabel(documentTypeOptions, value).toLowerCase().includes('section a');

export const isLiteracyScreeningSupportingDocumentType = (
  value: string | undefined | null,
  documentTypeOptions: SelectOption[] | undefined,
  screenerInvoiceOptions: SelectOption[] | undefined
): boolean => {
  if (findLiteracyScreeningOption(screenerInvoiceOptions, value)) {
    return false;
  }

  return optionLabel(documentTypeOptions, value)
    .toLowerCase()
    .includes('supporting');
};

export const isLiteracyScreeningSchoolInvoiceLevel = (
  value: string | undefined | null,
  invoiceLevelOptions: SelectOption[] | undefined
): boolean =>
  optionLabel(invoiceLevelOptions, value).toLowerCase() === 'school';

export const isLiteracyScreeningLeaInvoiceLevel = (
  value: string | undefined | null,
  invoiceLevelOptions: SelectOption[] | undefined
): boolean => optionLabel(invoiceLevelOptions, value).toLowerCase() === 'lea';

export const isLiteracyScreeningReimbursementYes = (
  value?: string | null
): boolean => normalize(value) === 'yes';

export const isLiteracyScreeningReimbursementNo = (
  value?: string | null
): boolean => normalize(value) === 'no';

export const isLiteracyScreeningApprovedStatus = (
  value?: string | null
): boolean => normalize(value) === 'approved';

export const isLiteracyScreeningDeniedStatus = (
  value?: string | null
): boolean => normalize(value) === 'denied';

export const isLiteracyScreeningPendingApprovalStatus = (
  value?: string | null
): boolean =>
  normalize(value) === normalize(LITERACY_SCREENING_PENDING_APPROVAL_STATUS);

export const isLiteracyScreeningFileBlockingPath = (
  file: Pick<LiteracyScreeningFileListItemModel, 'mdeStatus'>
): boolean => !isLiteracyScreeningDeniedStatus(file.mdeStatus);

export const canDeleteLiteracyScreeningUploadedFile = (
  file: Pick<LiteracyScreeningFileListItemModel, 'mdeStatus'>
): boolean =>
  !isLiteracyScreeningApprovedStatus(file.mdeStatus) &&
  !isLiteracyScreeningDeniedStatus(file.mdeStatus);

export const uniqueLiteracyScreeningSchoolOptions = (
  schools: LiteracyScreeningSchoolModel[] | undefined
): SelectOption[] => {
  const seen = new Set<string>();
  const options: SelectOption[] = [];

  (schools || []).forEach((school) => {
    const schoolIdentifier = (school.schoolIdentifier || '').trim();
    if (!schoolIdentifier) {
      return;
    }

    const key = schoolIdentifier.toLowerCase();
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    const schoolName = (school.schoolName || '').trim();
    options.push({
      value: schoolIdentifier,
      text: schoolName ? `${schoolName} ${schoolIdentifier}` : schoolIdentifier,
      isDisabled: false,
    });
  });

  return options.sort(compareOptionText);
};

const addUniqueScreener = (
  values: string[],
  seen: Set<string>,
  value?: string | null
): void => {
  const screener = (value || '').trim();
  if (!screener) {
    return;
  }

  const key = screener.toLowerCase();
  if (seen.has(key)) {
    return;
  }

  seen.add(key);
  values.push(screener);
};

const collectGradeBoyScreener = (
  grade: LiteracyScreeningGradesModel,
  values: string[],
  seen: Set<string>
): void => {
  const boySession = (grade.screenerSession || []).find((session) =>
    isBeginningOfYearSession(session.session)
  );
  addUniqueScreener(values, seen, grade.screener || boySession?.screener);
};

const collectSchoolLevelBoyScreeners = (
  sessions: LiteracyScreeningScreenerModel[] | undefined,
  values: string[],
  seen: Set<string>
): void => {
  (sessions || []).forEach((session) => {
    if (isBeginningOfYearSession(session.session)) {
      addUniqueScreener(values, seen, session.screener);
    }
  });
};

export const getLiteracyScreeningBoyScreenerValues = (
  school: LiteracyScreeningSchoolModel | undefined
): string[] => {
  const values: string[] = [];
  const seen = new Set<string>();

  (school?.grades || []).forEach((grade) => {
    collectGradeBoyScreener(grade, values, seen);
  });
  collectSchoolLevelBoyScreeners(school?.screener, values, seen);

  return values;
};

export const getLiteracyScreeningBoyScreenerPairs = (
  schools: LiteracyScreeningSchoolModel[] | undefined
): LiteracyScreeningBoyScreenerPair[] => {
  const pairs: LiteracyScreeningBoyScreenerPair[] = [];
  const seen = new Set<string>();

  (schools || []).forEach((school) => {
    const schoolIdentifier = (school.schoolIdentifier || '').trim();
    if (!schoolIdentifier) {
      return;
    }

    getLiteracyScreeningBoyScreenerValues(school).forEach((screener) => {
      const key = `${schoolIdentifier.toLowerCase()}|${screener.toLowerCase()}`;
      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      pairs.push({ schoolIdentifier, screener });
    });
  });

  return pairs;
};

export const hasLiteracyScreeningSavedBoyEntry = (
  schools: LiteracyScreeningSchoolModel[] | undefined
): boolean => getLiteracyScreeningBoyScreenerPairs(schools).length > 0;

const toScreenerOption = (
  value: string,
  screenerInvoiceOptions: SelectOption[] | undefined
): SelectOption => {
  const match = findLiteracyScreeningOption(screenerInvoiceOptions, value);
  return match
    ? { value: match.value, text: match.text, isDisabled: false }
    : { value, text: value, isDisabled: false };
};

const getSchoolBoyScreenerKey = (
  school: LiteracyScreeningSchoolModel,
  screenerInvoiceOptions: SelectOption[] | undefined
): string =>
  getLiteracyScreeningBoyScreenerValues(school)
    .map((screener) => {
      const option = toScreenerOption(screener, screenerInvoiceOptions);
      return normalize(option.value) || normalize(option.text);
    })
    .filter(Boolean)
    .sort()
    .join('|');

export const areLiteracyScreeningBoyScreenersUniformAcrossSchools = (
  schools: LiteracyScreeningSchoolModel[] | undefined,
  screenerInvoiceOptions?: SelectOption[]
): boolean => {
  const usedKeys = (schools || [])
    .filter((school) => (school.schoolIdentifier || '').trim())
    .map((school) => getSchoolBoyScreenerKey(school, screenerInvoiceOptions))
    .filter((key) => key.length > 0);

  if (usedKeys.length === 0) {
    return false;
  }

  return usedKeys.every((key) => key === usedKeys[0]);
};

export const getLiteracyScreeningBoyScreenerOptions = (
  schools: LiteracyScreeningSchoolModel[] | undefined,
  screenerInvoiceOptions: SelectOption[] | undefined,
  schoolIdentifier?: string | null
): SelectOption[] => {
  const normalizedSchool = normalize(schoolIdentifier);
  const sourceSchools = normalizedSchool
    ? (schools || []).filter(
        (school) => normalize(school.schoolIdentifier) === normalizedSchool
      )
    : schools || [];
  const seen = new Set<string>();
  const options: SelectOption[] = [];

  sourceSchools.forEach((school) => {
    getLiteracyScreeningBoyScreenerValues(school).forEach((screener) => {
      const option = toScreenerOption(screener, screenerInvoiceOptions);
      const key = normalize(option.value) || normalize(option.text);
      if (!key || seen.has(key)) {
        return;
      }

      seen.add(key);
      options.push(option);
    });
  });

  return options.sort(compareOptionText);
};

export const isLiteracyScreeningScreenerInvoiceFile = (
  file: Pick<LiteracyScreeningFileListItemModel, 'documentType'>,
  screenerInvoiceOptions: SelectOption[] | undefined
): boolean =>
  Boolean(
    findLiteracyScreeningOption(screenerInvoiceOptions, file.documentType)
  );

export const isLiteracyScreeningLeaInvoiceFile = (
  file: Pick<
    LiteracyScreeningFileListItemModel,
    'documentType' | 'schoolIdentifier' | 'leaIdentifier'
  >,
  screenerInvoiceOptions: SelectOption[] | undefined
): boolean => {
  if (!isLiteracyScreeningScreenerInvoiceFile(file, screenerInvoiceOptions)) {
    return false;
  }

  const schoolIdentifier = (file.schoolIdentifier || '').trim();
  const leaIdentifier = (file.leaIdentifier || '').trim();
  return (
    !schoolIdentifier ||
    (!!leaIdentifier &&
      normalize(schoolIdentifier) === normalize(leaIdentifier))
  );
};

export const isLiteracyScreeningMatchingScreener = (
  fileDocumentType: string | undefined | null,
  screenerValue: string | undefined | null,
  screenerInvoiceOptions: SelectOption[] | undefined
): boolean => {
  const fileOption = findLiteracyScreeningOption(
    screenerInvoiceOptions,
    fileDocumentType
  );
  const selectedOption = findLiteracyScreeningOption(
    screenerInvoiceOptions,
    screenerValue
  );

  if (fileOption && selectedOption) {
    return normalize(fileOption.value) === normalize(selectedOption.value);
  }

  return normalize(fileDocumentType) === normalize(screenerValue);
};

export const hasBlockingLiteracyScreeningSchoolScreenerInvoice = (
  files: LiteracyScreeningFileListItemModel[] | undefined,
  schoolIdentifier: string | undefined | null,
  screenerValue: string | undefined | null,
  screenerInvoiceOptions: SelectOption[] | undefined
): boolean => {
  const normalizedSchool = normalize(schoolIdentifier);
  if (!normalizedSchool || !normalize(screenerValue)) {
    return false;
  }

  return (files || []).some(
    (file) =>
      isLiteracyScreeningFileBlockingPath(file) &&
      normalize(file.schoolIdentifier) === normalizedSchool &&
      isLiteracyScreeningMatchingScreener(
        file.documentType,
        screenerValue,
        screenerInvoiceOptions
      )
  );
};

export const getLiteracyScreeningUploadDocumentType = (
  documentType: string,
  screenerInvoice: string,
  documentTypeOptions: SelectOption[] | undefined
): string => {
  if (
    isLiteracyScreeningScreenerInvoiceDocumentType(
      documentType,
      documentTypeOptions
    )
  ) {
    return screenerInvoice;
  }

  return documentType;
};

export interface LiteracyScreeningDocumentDetailLines {
  primary: string;
  secondary?: string;
}

export const getLiteracyScreeningDocumentDetailLines = (
  file: LiteracyScreeningFileListItemModel,
  documentTypeOptions: SelectOption[] | undefined,
  screenerInvoiceOptions: SelectOption[] | undefined
): LiteracyScreeningDocumentDetailLines => {
  const screener = findLiteracyScreeningOption(
    screenerInvoiceOptions,
    file.documentType
  );
  const isSectionA = isLiteracyScreeningSectionADocumentType(
    file.documentType,
    documentTypeOptions
  );
  const isSupporting = isLiteracyScreeningSupportingDocumentType(
    file.documentType,
    documentTypeOptions,
    screenerInvoiceOptions
  );

  if (screener || (!isSectionA && !isSupporting && file.documentType)) {
    const screenerName = screener?.text || (file.documentType || '').trim();
    const schoolIdentifier = (file.schoolIdentifier || '').trim();
    return {
      primary: screenerName,
      secondary: schoolIdentifier || undefined,
    };
  }

  return {
    primary: optionLabel(documentTypeOptions, file.documentType),
  };
};

export const getLiteracyScreeningDocumentDetails = (
  file: LiteracyScreeningFileListItemModel,
  documentTypeOptions: SelectOption[] | undefined,
  screenerInvoiceOptions: SelectOption[] | undefined
): string => {
  const { primary, secondary } = getLiteracyScreeningDocumentDetailLines(
    file,
    documentTypeOptions,
    screenerInvoiceOptions
  );
  return secondary ? `${primary} – ${secondary}` : primary;
};

export const mergeLiteracyScreeningMdeStatusOptions = (
  options: SelectOption[] | undefined,
  files: LiteracyScreeningFileListItemModel[] | undefined
): SelectOption[] => {
  const next = [...(options || [])];
  const seen = new Set(next.map((option) => normalize(option.value)));

  (files || []).forEach((file) => {
    const status = (file.mdeStatus || '').trim();
    if (!status || seen.has(normalize(status))) {
      return;
    }

    seen.add(normalize(status));
    next.push({
      value: status,
      text: status,
      isDisabled: false,
    });
  });

  return next;
};

export const getLiteracyScreeningDisplayedMdeStatus = (
  value?: string | null
): string => {
  if (isLiteracyScreeningApprovedStatus(value)) {
    return 'Approved';
  }
  if (isLiteracyScreeningDeniedStatus(value)) {
    return 'Denied';
  }

  return LITERACY_SCREENING_PENDING_APPROVAL_STATUS;
};

export const sortLiteracyScreeningFilesNewestFirst = (
  files: LiteracyScreeningFileListItemModel[] | undefined
): LiteracyScreeningFileListItemModel[] =>
  [...(files || [])].sort((left, right) => {
    const leftTime = Date.parse(left.uploadedDate || '') || 0;
    const rightTime = Date.parse(right.uploadedDate || '') || 0;
    return rightTime - leftTime;
  });

export const getEnabledLiteracyScreeningOptions = (
  optionSets: Record<string, SelectOption[]>,
  key: string
): SelectOption[] =>
  (optionSets[key] || []).filter((option) => !option.isDisabled);

export const getLiteracyScreeningUploadFieldOptions = ({
  documentTypeOptions,
  invoiceLevelOptions,
  screenerInvoiceOptions,
  schools,
  files,
  reimbursement,
  selectedInvoiceLevel,
  selectedSchool,
}: LiteracyScreeningUploadFieldOptionsInput): LiteracyScreeningUploadFieldOptions => {
  const blockingFiles = (files || []).filter(
    isLiteracyScreeningFileBlockingPath
  );
  const boyPairs = getLiteracyScreeningBoyScreenerPairs(schools);
  const unusedSchoolPaths = boyPairs.filter(
    (pair) =>
      !hasBlockingLiteracyScreeningSchoolScreenerInvoice(
        blockingFiles,
        pair.schoolIdentifier,
        pair.screener,
        screenerInvoiceOptions
      )
  );
  const schoolOptionsBase = uniqueLiteracyScreeningSchoolOptions(schools);
  const usedScreenerInvoiceOptions = getLiteracyScreeningBoyScreenerOptions(
    schools,
    screenerInvoiceOptions
  );
  const hasUniformBoyScreeners =
    areLiteracyScreeningBoyScreenersUniformAcrossSchools(
      schools,
      screenerInvoiceOptions
    );
  const hasSavedBoyEntry = boyPairs.length > 0;
  const hasBlockingSectionA = blockingFiles.some((file) =>
    isLiteracyScreeningSectionADocumentType(
      file.documentType,
      documentTypeOptions
    )
  );
  const hasBlockingLeaInvoice = blockingFiles.some((file) =>
    isLiteracyScreeningLeaInvoiceFile(file, screenerInvoiceOptions)
  );
  const allScreenerInvoicePathsUsed =
    hasBlockingLeaInvoice && unusedSchoolPaths.length === 0;
  const isReimbursementYes = isLiteracyScreeningReimbursementYes(reimbursement);
  const isSchoolLevelSelected = isLiteracyScreeningSchoolInvoiceLevel(
    selectedInvoiceLevel,
    invoiceLevelOptions
  );

  const nextDocumentTypeOptions = documentTypeOptions
    .filter(
      (option) =>
        isReimbursementYes ||
        !isLiteracyScreeningScreenerInvoiceDocumentType(
          option.value,
          documentTypeOptions
        )
    )
    .map((option) => {
      if (
        isLiteracyScreeningScreenerInvoiceDocumentType(
          option.value,
          documentTypeOptions
        )
      ) {
        return {
          ...option,
          isDisabled: !hasSavedBoyEntry || allScreenerInvoicePathsUsed,
        };
      }

      if (
        isLiteracyScreeningSectionADocumentType(
          option.value,
          documentTypeOptions
        )
      ) {
        return {
          ...option,
          isDisabled: !hasSavedBoyEntry || hasBlockingSectionA,
        };
      }

      return { ...option, isDisabled: false };
    });

  const nextInvoiceLevelOptions = invoiceLevelOptions.map((option) => {
    if (isLiteracyScreeningLeaInvoiceLevel(option.value, invoiceLevelOptions)) {
      return {
        ...option,
        isDisabled: hasBlockingLeaInvoice || !hasUniformBoyScreeners,
      };
    }

    if (
      isLiteracyScreeningSchoolInvoiceLevel(option.value, invoiceLevelOptions)
    ) {
      return { ...option, isDisabled: unusedSchoolPaths.length === 0 };
    }

    return { ...option, isDisabled: false };
  });

  const nextSchoolOptions = schoolOptionsBase.map((option) => {
    const remainingForSchool = unusedSchoolPaths.some(
      (pair) => normalize(pair.schoolIdentifier) === normalize(option.value)
    );
    return {
      ...option,
      isDisabled: !remainingForSchool,
    };
  });

  const nextScreenerInvoiceOptions = usedScreenerInvoiceOptions.map(
    (option) => ({
      ...option,
      isDisabled:
        isSchoolLevelSelected &&
        hasBlockingLiteracyScreeningSchoolScreenerInvoice(
          blockingFiles,
          selectedSchool,
          option.value,
          screenerInvoiceOptions
        ),
    })
  );

  return {
    documentTypeOptions: nextDocumentTypeOptions,
    invoiceLevelOptions: nextInvoiceLevelOptions,
    schoolOptions: nextSchoolOptions,
    screenerInvoiceOptions: nextScreenerInvoiceOptions,
    hasSavedBoyEntry,
  };
};

export const createLiteracyScreeningUploadValidationSchema = (
  documentTypeOptions: SelectOption[],
  invoiceLevelOptions: SelectOption[]
): yup.AnyObjectSchema =>
  yup.object().shape({
    documentType: yup.string(),
    invoiceLevel: yup
      .string()
      .test(
        'invoice-level-required',
        VALIDATION_MESSAGE.LITERACY_SCREENING_INVOICE_LEVEL_REQUIRED,
        function (value) {
          if (
            !isLiteracyScreeningScreenerInvoiceDocumentType(
              this.parent.documentType,
              documentTypeOptions
            )
          ) {
            return true;
          }

          return Boolean((value || '').trim());
        }
      ),
    school: yup
      .string()
      .test(
        'school-required',
        VALIDATION_MESSAGE.LITERACY_SCREENING_SCHOOL_REQUIRED,
        function (value) {
          if (
            !isLiteracyScreeningScreenerInvoiceDocumentType(
              this.parent.documentType,
              documentTypeOptions
            ) ||
            !isLiteracyScreeningSchoolInvoiceLevel(
              this.parent.invoiceLevel,
              invoiceLevelOptions
            )
          ) {
            return true;
          }

          return Boolean((value || '').trim());
        }
      ),
    screenerInvoice: yup
      .string()
      .test(
        'screener-invoice-required',
        VALIDATION_MESSAGE.LITERACY_SCREENING_SCREENER_INVOICE_REQUIRED,
        function (value) {
          if (
            !isLiteracyScreeningScreenerInvoiceDocumentType(
              this.parent.documentType,
              documentTypeOptions
            )
          ) {
            return true;
          }

          const isSchoolLevel = isLiteracyScreeningSchoolInvoiceLevel(
            this.parent.invoiceLevel,
            invoiceLevelOptions
          );
          const isLeaLevel = isLiteracyScreeningLeaInvoiceLevel(
            this.parent.invoiceLevel,
            invoiceLevelOptions
          );

          if (isLeaLevel) {
            return Boolean((value || '').trim());
          }

          if (isSchoolLevel && (this.parent.school || '').trim()) {
            return Boolean((value || '').trim());
          }

          return true;
        }
      ),
  });
